//@flow
import React from "react"
import ReactGA from "react-ga4"
import { connect } from "react-redux"
import { compose } from "react-apollo"
import { Route, Redirect } from "react-router-dom"
import { FormattedMessage } from "react-intl"
import styled from "styled-components"
import { TweenMax, Cubic, Back } from "gsap"
import toSlugCase from "to-slug-case"
import { Flex } from "../UI/Page"
import { ModalSwitch } from "../Modal"
import KioskBackground from "./KioskBackground"
import KioskNavBar, { KioskNavBarLink } from "./KioskNavBar"
import KioskContent from "./KioskContent"
import AttractLoopView from "./AttractLoopView"
import Navigator from "./Navigator"
import LanguageModal from "./LanguageModal"
import HistoryModal from "./HistoryModal"
import LocationsModal from "./LocationsModal"
import GlossaryModal from "./GlossaryModal"
import SurveyModal from "./SurveyModal"
import ConfirmationModal from "./ConfirmationModal"
import { isModal } from "../Modal"
import FixedResolutionContainer, {
  setScaleAndOffsets,
  currentScaleFactor
} from "./FixedResolutionContainer"
import { withCurrentKioskData, withCAMMForecast } from "./queries"
import {
  restartSession,
  extendSession,
  sessionLastTouched,
  sessionStartTime
} from "./redux"

import type { CurrentKiosk, CAMMForecast } from "./queries"
import type { SetScale } from "./FixedResolutionContainer"
import type { RouterHistory, Location } from "react-router-dom"

import mcaqd from "./maricopa.svg"

const BaseContainer = styled.div`
  display: block;
  margin: 0;
  position: relative;
  height: 100%;
  width: 100%;

  & button:focus {
    outline: 0;
  }
`

type Props = {
  currentLocale: string,
  history: RouterHistory,
  location: Location,
  isModal: boolean,
  data: CurrentKiosk,
  camm: CAMMForecast,
  setScale: SetScale,
  currentScaleFactor: number,
  setScaleAndOffsets: SetScale,
  extendSession: Function,
  restartSession: Function,
  sessionLastTouched: number,
  sessionStartTime: number
}

ReactGA.initialize(process.env.REACT_APP_GA4_ID)

class Kiosk extends React.Component<Props> {
  container: ?HTMLElement
  content: ?HTMLElement
  navBar: ?HTMLElement
  interval: number

  constructor(props: Props) {
    super(props)
    this.container = null
    this.navBar = null
    this.content = null
    this.interval = 0
  }

  componentDidUpdate(prevProps: Props) {
    const { isModal, sessionLastTouched, extendSession, data } = this.props
    if (prevProps.isModal !== isModal) {
      TweenMax.to(this.navBar, 0.35, {
        y: isModal ? "+=100" : 0,
        ease: Back.easeOut
      })
      TweenMax.to(this.navBar, 0.35, {
        opacity: isModal ? 0.1 : 1,
        ease: Cubic.easeOut
      })
      TweenMax.to(this.content, 0.35, {
        y: isModal ? "+=50" : 0,
        ease: Cubic.easeOut
      })
    }

    if (prevProps.sessionLastTouched === sessionLastTouched) {
      extendSession()
      const shortName =
        (data.currentKiosk && data.currentKiosk.shortName) || "unknown"
      if (shortName !== "unknown") {
        const pageURL =
          `/${toSlugCase(shortName)}` +
          window.location.pathname +
          window.location.search
        const title = [shortName, window.location.pathname.split("/").pop()].join(" - ")
        ReactGA.send({ hitType: "pageview", page: pageURL, title});
        console.log(`Should have tracked: ${pageURL} ${title}`)
      }
    }
  }

  componentDidMount = () => {
    const { data, camm } = this.props
    data.startPolling(1000 * 60 * 5)
    camm.startPolling(1000 * 60 * 15)
    window && window.addEventListener("resize", this.handleWindowResize)
    this.handleWindowResize()
    this.interval = setInterval(() => this.restartIfNecessary(), 1000 * 10)
  }

  componentWillUnmount = () => {
    const { data, camm } = this.props
    data.stopPolling()
    camm.stopPolling()
    window && window.removeEventListener("resize", this.handleWindowResize)
    clearInterval(this.interval)
  }

  restartIfNecessary = () => {
    const { sessionLastTouched, restartSession, history: { push } } = this.props
    const idleTime = new Date().getTime() / 1000 - sessionLastTouched
    console.log(
      `[debug] will restart in ${120 - idleTime} seconds if not interaction.`
    )
    if (idleTime > 120) {
      restartSession()
      push("/kiosk/home/attract")
    }
  }

  handleWindowResize = () => {
    const { currentScaleFactor, setScaleAndOffsets } = this.props
    if (!window || !this.container || this.container === null) {
      return
    }
    const width = this.container.offsetWidth || 1920
    const height = this.container.offsetHeight || 1080
    const verticalRatio = window.innerHeight / height
    const horizontalRatio = window.innerWidth / width
    const newScale = Math.min(verticalRatio, horizontalRatio, 1)
    if (currentScaleFactor !== newScale) {
      const x = window.innerWidth - width * newScale
      const y = window.innerHeight - height * newScale
      setScaleAndOffsets(newScale, x, y)
    }
  }

  handleLocaleToggle = (e: Event) => {
    const { history: { push } } = this.props
    const path = {
      pathname: `/kiosk/confirm/language`,
      state: {
        modal: true
      }
    }
    e.preventDefault()
    push(path)
  }

  render() {
    const { currentLocale, currentScaleFactor, ...props } = this.props
    return (
      <KioskBackground>
        <FixedResolutionContainer
          innerRef={ref => (this.container = ref)}
          scale={currentScaleFactor}
        >
          <KioskContent innerRef={el => (this.content = el)}>
            <ModalSwitch
              {...props}
              Container={BaseContainer}
              ModalRoutes={[
                <Route
                  key="confirmLocale"
                  path="/kiosk/confirm/language"
                  component={LanguageModal}
                />,
                <Route
                  key="surveyLocale"
                  path="/kiosk/survey"
                  component={SurveyModal}
                />,
                <Route
                  key="contactLocale"
                  path="/kiosk/confirm/contact/:type"
                  component={ConfirmationModal}
                />,
                <Route
                  key="viewHistory"
                  path="/kiosk/history"
                  component={HistoryModal}
                />,
                <Route
                  key="viewLocations"
                  path="/kiosk/locations"
                  component={LocationsModal}
                />,
                <Route
                  key="viewLocations"
                  path="/kiosk/glossary/:metric"
                  component={GlossaryModal}
                />
              ]}
            >
              <Route path="/kiosk/home/:scene" component={AttractLoopView} />
              <Route path="/kiosk/section" component={Navigator} />
              <Route render={() => <Redirect to="/kiosk/home/attract" />} />
            </ModalSwitch>
          </KioskContent>
          <KioskNavBar innerRef={el => (this.navBar = el)}>
            <KioskNavBarLink onClick={this.handleLocaleToggle} href="#">
              <FormattedMessage id="kiosk.language.alternate" />
            </KioskNavBarLink>
            <Flex grow={1} />
            <Flex width="45px" />
            <img
              src={mcaqd}
              alt="Maricopa County Air Quality Department"
              width="315"
              height="86"
            />
          </KioskNavBar>
        </FixedResolutionContainer>
      </KioskBackground>
    )
  }
}

const KioskWithData = compose(withCurrentKioskData, withCAMMForecast)(Kiosk)

export { WaitingToRideScene } from "./CommitView"
export { reducer } from "./redux"

export default connect(
  state => ({
    isModal: isModal(state),
    currentScaleFactor: currentScaleFactor(state),
    sessionLastTouched: sessionLastTouched(state),
    sessionStartTime: sessionStartTime(state)
  }),
  { setScaleAndOffsets, extendSession, restartSession }
)(KioskWithData)



// WEBPACK FOOTER //
// ./src/Kiosk/Kiosk.js